import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LogOut,
  ChevronRight,
  ChevronUp,
  Bookmark,
  Compass,
  FileText,
  BarChart3,
  TrendingUp,
  Home,
  Video,
  Rocket,
  Search,
  Package,
  CreditCard,
  Settings as SettingsIcon,
  Users,
  ScrollText,
  Inbox,
  MessageSquare,
  Mail,
  Menu,
  X,
  HelpCircle,
  Check,
  LayoutGrid,
  Plus,
} from 'lucide-react'
import { useBrand } from '../hooks/useBrand'
import { useAuth } from '../hooks/useAuth'
import { useAccount } from '../hooks/useAccount'
import { createAgencyCheckout, AGENCY_CHECKOUT_RETURN_URL } from '../lib/api'
import { onAppRevalidate } from '../lib/revalidate'
import { useSidebarBadges } from '../hooks/useSidebarBadges'
import { onWalletChanged } from '../lib/wallet'
import WhopCheckoutModal, { type WhopCheckoutParams } from './WhopCheckoutModal'
import DevTools from './DevTools'
import type { LucideIcon } from 'lucide-react'
import { isHiringEnabled, isMessagesEnabled } from '../lib/featureFlags'
import { styles } from './Sidebar.styles'

// The badge counts live in useSidebarBadges, which owns their cadence. This is
// the plan/brand reload: much heavier, and it changes far more rarely.
const PLAN_REVALIDATE_MS = 5 * 60_000

interface SidebarProps {
  signOut: () => void
  // Suppresses the mobile hamburger toggle while the Onboarding overlay is up.
  // The overlay sits at z=100; the toggle lives at z=1010, so without this it
  // pokes through on viewports ≤1000px and the user can pop a non-functional
  // sidebar over a flow they haven't completed yet.
  hideMobileToggle?: boolean
}

interface NavItem {
  to: string
  icon: LucideIcon
  label: string
  end: boolean
  badge?: string
  /** Numeric notification count (pending applications / samples). Rendered as
      a small pink superscript badge immediately after the label text — NOT as
      a trailing pill like `badge` (client request 2026-07-09). */
  count?: string
  /** When true, render as a non-clickable gray row with a "Coming soon" pill. */
  disabled?: boolean
  /** When true, render a pink "New" pill (matches the Team / Activity log
      items in the brand menu). */
  isNew?: boolean
  /** When true, render with extra visual weight (pink tint) to stand out
      among neighbors. Used for BMC Max so it pops above disabled siblings. */
  featured?: boolean
}

interface NavSection {
  title: string
  items: NavItem[]
}

function getNavSections(brandType?: 'tiktok_shop' | 'ugc', hiringEnabled: boolean = true, applicationsCount?: number, pendingSamplesCount?: number, messagesUnread: number = 0, messagesEnabled: boolean = false, supportUnread: number = 0): NavSection[] {
  const sections: NavSection[] = []

  // Home — no section title.
  //
  // Wallet is deliberately NOT a nav row. Credit lives inside Billing (its
  // balances + history render there), and the spendable balance is surfaced by
  // WalletCreditChip in the header of the pages where hiring actually
  // happens — Find Creators, Applications, Listings. A brand doesn't navigate
  // "to their wallet"; they need to know the balance while choosing a creator.
  sections.push({
    title: '',
    items: [
      { to: '/', icon: Home, label: 'Home', end: true },
    ],
  })

  // Hiring section. Restructured 2026-05-02 (post-IA-redesign discussion):
  //   - "Hires" page removed — that data now lives inside each campaign as
  //     the Performance tab (creators-as-line-items, Meta dashboard style).
  //   - "Applications" page removed — incoming applications fold into the
  //     "Creators" page as a tab alongside Browse.
  //   - "Discover Creators" → "Creators" (since it now also handles incoming
  //     applications, "Discover" no longer accurately describes the page).
  //   - "Billing" removed from main nav — moved into the brand menu at the
  //     bottom of the sidebar (same place as Settings + Sign Out) per the
  //     2026-05-02 sidebar redesign. Account/billing actions feel like
  //     "user/account stuff" rather than primary product navigation.
  // The /hires and /applications routes are intentionally still live in
  // App.tsx so any existing deep links / external bookmarks don't 404 — both
  // are now redirects into the pages that replaced them (/hires resolves the
  // legacy ?status= into the campaign's Performance tab; see HiresRedirect).
  sections.push({
    title: 'Campaigns',
    items: [
      { to: '/campaigns', icon: BarChart3, label: 'Listings', end: false, disabled: !hiringEnabled },
      { to: '/roster', icon: Search, label: 'Find Creators', end: true, disabled: !hiringEnabled },
      { to: '/roster/invited', icon: Mail, label: 'Invited Creators', end: true, disabled: !hiringEnabled },
      {
        to: '/roster/applications', icon: Inbox, label: 'Applications', end: true, disabled: !hiringEnabled,
        count: applicationsCount ? String(applicationsCount) : undefined,
      },
      {
        to: '/samples', icon: Package, label: 'Sample Requests', end: false, disabled: !hiringEnabled, isNew: true,
        count: pendingSamplesCount ? String(pendingSamplesCount) : undefined,
      },
      { to: '/hired-creators', icon: Users, label: 'Hired Creators', end: true, disabled: !hiringEnabled },
      // { to: '/videos', icon: Video, label: 'Submitted Videos', end: true, disabled: !hiringEnabled },
      // Messages nav item — gated behind the VITE_ENABLE_MESSAGES flag. Omitted
      // entirely (not just disabled) while messages are off.
      ...(messagesEnabled ? [{
        to: '/messages', icon: MessageSquare, label: 'Messages', end: false, disabled: !hiringEnabled,
        // Same treatment as Applications' count — a small pink badge glued to
        // the label text, not a trailing pill (client request 2026-07-21).
        count: messagesUnread > 0 ? String(messagesUnread) : undefined,
      }] : []),
    ],
  })

  // Analytics section — /analytics is a real, data-backed page as of
  // 2026-07-28 (it was a blurred "coming soon" teaser before, which is why
  // this row used to carry a "Soon" badge). Always visible, independent of the
  // hiring flag and brand_type; brands with no hires yet get an empty state.
  sections.push({
    title: 'Analytics',
    items: [
      { to: '/analytics', icon: TrendingUp, label: 'ROI & Analytics', end: true },
    ],
  })

  // Legacy section — kept hidden behind a flag in case we re-surface it,
  // but removed from the V1 nav. Files preserved at /hire, /active-campaigns,
  // /videos.

  // Intelligence section — TikTok Shop brands
  if (brandType !== 'ugc') {
    sections.push({
      title: 'Intelligence',
      items: [
        { to: '/discover', icon: Compass, label: 'Video Discovery', end: true },
        { to: '/playbook', icon: Bookmark, label: 'Playbook', end: true },
      ],
    })
  }

  // Support section — links to the /support page (ticket list + chat, same
  // two-pane shell as Messages). Moved here from the brand menu popover at the
  // bottom of the sidebar so it's a first-class, always-visible nav entry.
  sections.push({
    title: 'Support',
    items: [
      {
        to: '/support', icon: HelpCircle, label: 'Need Help?', end: true,
        // Same treatment as Applications/Samples/Messages — a small pink
        // badge glued to the label when BMC support has replied and the
        // brand hasn't read it yet.
        count: supportUnread > 0 ? String(supportUnread) : undefined,
      },
    ],
  })

  return sections
}

// Nav shown at the AGENCY LOBBY (agency account, not inside a brand). The
// brand-scoped nav (Campaigns/Intel/…) is meaningless there — these are the
// agency's OWN pages. The Brands roster is visible to anyone at the lobby.
// Team is owner OR admin (an agency admin manages people but not money);
// Billing + Settings stay owner-only. Every one of these pages also redirects
// on its own, so this is presentation, not the gate.
/** `canManage` is the owner-or-co-owner test (useAccount.canManageAccount), not
 *  ownership: Billing and Settings are exactly what the co-owner role adds over
 *  an admin, and the worker gates both on the same pair (accountPrincipal). */
function getAgencyNavSections(canManage: boolean, canView: boolean): NavSection[] {
  const items: NavItem[] = [
    { to: '/agency', icon: LayoutGrid, label: 'Brands', end: true },
  ]
  // canView includes the read-only viewer role: both pages are reads, and the
  // Team page renders without its action buttons for them.
  if (canView) {
    items.push({ to: '/agency/team', icon: Users, label: 'Team', end: true, isNew: true })
    items.push({ to: '/agency/activity', icon: ScrollText, label: 'Activity log', end: true, isNew: true })
  }
  if (canManage) {
    items.push({ to: '/agency/billing', icon: CreditCard, label: 'Billing', end: true })
    items.push({ to: '/agency/settings', icon: SettingsIcon, label: 'Settings', end: true })
  }
  return [{ title: '', items }]
}

function PlanBadge() {
  // Plan + credit come straight off the shared brand context (brand_configs
  // is loaded with select('*')). The badge used to run its own fetch plus a
  // ?from=checkout poll here, but that only updated this component's local
  // state while HiringGate kept the stale row — the post-checkout poll now
  // lives in CheckoutActivation, which refreshes the shared context so the
  // badge and the gates flip together.
  // Credit used to be shown here as a second line. It moved to the Wallet nav
  // item at the top of the sidebar — one home for the balance, and a row the
  // brand can actually click through to.
  const { brand, reloadSilent } = useBrand()
  const [showUpgrade, setShowUpgrade] = useState(false)
  const planTier = brand?.plan_tier ?? 'free'

  // Re-fetch when the tab regains focus (covers the case where the user upgraded
  // in another tab and returned). reloadSilent is the FULL brand reload — three
  // to five Supabase reads — so it is throttled hard: a plan change is a rare
  // event, and this used to fire on literally every alt-tab.
  useEffect(() => onAppRevalidate(() => { void reloadSilent() }, PLAN_REVALIDATE_MS), [reloadSilent])

  const planLabel = planTier === 'free' ? 'Free' : planTier === 'starter' ? 'Starter' : planTier === 'pro' ? 'Pro' : planTier === 'pro_annual' ? 'Pro (Annual)' : planTier === 'agency' ? 'Agency' : planTier
  const planColor = planTier === 'free' ? '#6b7280' : '#FB3AAE'
  const canUpgrade = planTier === 'free' || planTier === 'starter'

  return (
    <>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '10px 14px',
        margin: '8px 12px 0',
        background: 'var(--bg-secondary)',
        borderRadius: 8,
        fontSize: 12,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Plan</span>
          <span style={{ color: planColor, fontWeight: 700 }}>{planLabel}</span>
        </div>
        {canUpgrade && brand && (
          <button
            onClick={() => setShowUpgrade(true)}
            style={{
              marginTop: 4,
              padding: '6px 10px',
              background: '#FB3AAE',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Upgrade to Pro
          </button>
        )}
      </div>
      {showUpgrade && brand && (
        <UpgradeModalLazy
          brandId={brand.id}
          currentTier={planTier}
          onClose={() => setShowUpgrade(false)}
        />
      )}
    </>
  )
}

// Lazy-load the modal so the sidebar doesn't pull it on initial render.
function UpgradeModalLazy(props: { brandId: string; currentTier: string; onClose: () => void }) {
  const [Mod, setMod] = useState<React.ComponentType<typeof props> | null>(null)
  useEffect(() => {
    import('./UpgradeModal').then(m => setMod(() => m.default as React.ComponentType<typeof props>))
  }, [])
  if (!Mod) return null
  return <Mod {...props} />
}

export default function Sidebar({ signOut, hideMobileToggle = false }: SidebarProps) {
  const { brand, reloadSilent } = useBrand()
  const { user } = useAuth()
  const userId = user?.id ?? null
  const { isAgency, canManageAccount, canViewAgency, inBrand } = useAccount()
  const hiringEnabled = isHiringEnabled()
  const messagesEnabled = isMessagesEnabled()
    // The brand-scoped nav (Campaigns, Hiring, Intel…) is meaningless at the agency
  // lobby (no active brand). At the lobby we swap in the agency's OWN nav
  // (Brands / Billing / Settings); it returns to the brand nav once they enter a
  // brand. Solo brands always get the brand nav.
  const atAgencyLobby = isAgency && !inBrand

  // All three nav badges in ONE request — see hooks/useSidebarBadges.ts. Each
  // badge used to own an effect, a fetch and a focus listener of its own, so
  // painting the nav cost four requests, two of them unbounded selects fetched
  // purely so the browser could count rows.
  const badges = useSidebarBadges(brand?.id, userId, {
    messagesEnabled: hiringEnabled && messagesEnabled,
  })

  // Reload the shared brand context when credit moves in THIS tab. The sidebar
  // no longer shows a balance itself, but it is the one component mounted on
  // every screen, so it stays the owner of this listener: WalletCreditChip and
  // the hire modals read account_credit_cents / credit_held_cents off the same
  // context, and focus-based refresh (in PlanBadge) never fires for a hire made
  // from the dashboard — no focus change happens.
  useEffect(() => onWalletChanged(() => { void reloadSilent() }), [reloadSilent])

  const navSections = atAgencyLobby
    ? getAgencyNavSections(canManageAccount, canViewAgency)
    : getNavSections(brand?.brand_type, hiringEnabled, badges.applications ?? undefined, badges.samples ?? undefined, badges.unread, messagesEnabled, badges.supportUnread)
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  // Close sidebar on route change (user tapped a nav link)
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [mobileOpen])

  const toggleMobile = useCallback(() => setMobileOpen(p => !p), [])


  return (
    <>
      {/* Mobile hamburger button — fixed top-left, only visible on small screens.
          Always shows Menu icon; the close button lives inside the sidebar.
          Suppressed during onboarding so it doesn't poke through the overlay. */}
      {!mobileOpen && !hideMobileToggle && (
        <button
          className="sidebar-mobile-toggle"
          onClick={toggleMobile}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      )}

      {/* The mobile wallet chip lived here (fixed, top-right, above 1000px
          breakpoint). Removed with the Wallet nav row: the balance is now shown
          inline in each hiring page's header by WalletCreditChip, which is
          visible at every width and doesn't have to dodge the hamburger. */}

      {/* Overlay behind sidebar on mobile */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

    <aside style={styles.sidebar} className={`sidebar-aside${mobileOpen ? ' sidebar-open' : ''}`}>
      <div style={styles.top}>
        {/* Logo row with mobile close button */}
        <div style={styles.logoRow}>
          <img
            src="/logo-dark.png"
            alt="Brands Meet Creators"
            style={{ height: '45px', width: 'auto' }}
          />
          {/* Close button — only visible on mobile, positioned at the end of the logo row */}
          <button
            className="sidebar-mobile-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Brand identity + plan badge moved to the bottom of the sidebar.
            See <BrandMenu /> further down — that component owns brand logo,
            plan tier, and the Settings / Billing / Sign Out menu. */}

        {/* Nav sections */}
        <nav style={styles.nav}>
          {navSections.map((section, si) => {
            // Section is "coming-soon" when every NON-featured item is
            // disabled. Featured items (e.g. BMC Max) are always-on upsells
            // that we still want to surface even when their siblings are
            // gated — they shouldn't suppress the section-level pill.
            const nonFeatured = section.items.filter(i => !i.featured)
            const sectionDisabled = nonFeatured.length > 0 && nonFeatured.every(i => i.disabled)
            return (
              <div key={si} style={styles.sectionGroup}>
                {section.title && (
                  <div style={{
                    ...styles.sectionLabel,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span>{section.title}</span>
                    {sectionDisabled && (
                      <span style={styles.comingSoonPill}>Coming soon</span>
                    )}
                  </div>
                )}
                {section.items.map((item) => {
                  const { to, icon: Icon, label, end, badge, count, disabled, featured, isNew } = item
                  if (disabled) {
                    return (
                      <div
                        key={to}
                        style={{
                          ...styles.navLink,
                          cursor: 'not-allowed',
                          opacity: 0.4,
                        }}
                        aria-disabled="true"
                        title="Coming soon"
                      >
                        <Icon size={16} strokeWidth={1.5} />
                        <span style={styles.navLabel}>{label}</span>
                        {isNew && <span style={styles.newBadge}>New</span>}
                      </div>
                    )
                  }
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      end={end}
                      className={({ isActive }) =>
                        `sidebar-nav-link${isActive ? ' active' : ''}`
                      }
                      style={({ isActive }) => ({
                        ...styles.navLink,
                        ...(featured ? styles.navFeatured : {}),
                        ...(isActive ? styles.navActive : {}),
                      })}
                    >
                      {({ isActive }) => (
                        <>
                          {/* Icon color: pink accent on featured items (BMC Max);
                              brand-purple on the active page so the accent
                              carries the "this is selected" signal alongside the
                              purple fill + left bar from styles.navActive. */}
                          <Icon size={16} strokeWidth={isActive ? 2 : 1.5}
                            color={isActive ? '#d72f92' : featured ? '#FB3AAE' : undefined}
                          />
                          {/* Label block — the text truncates with an ellipsis
                              if space runs out; the notification count renders
                              as a pink superscript badge glued to the end of
                              the word (client request), so it never pushes the
                              label to a second line. On rows that ALSO carry a
                              New pill (Sample Requests) the label can still
                              truncate, so the count collapses on row hover to
                              reveal the full text (.nav-count-collapsible in
                              globals.css); the New pill stays visible. */}
                          <span style={styles.navLabelWrap}>
                            <span style={styles.navLabelText}>{label}</span>
                            {count && (
                              <span
                                className={isNew ? 'nav-count-collapsible' : undefined}
                                style={styles.countBadge}
                              >
                                {count}
                              </span>
                            )}
                          </span>
                          {/* Trailing pills (text badges like "Soon" + the New
                              pill) cluster with a tight 4px gap — at the row's
                              11px gap the pair overflowed the 260px sidebar. */}
                          {(badge || isNew) && (
                            <span style={styles.badgeCluster}>
                              {badge && (
                                <span style={featured ? styles.badgeFeatured : styles.badge}>{badge}</span>
                              )}
                              {isNew && <span style={styles.newBadge}>New</span>}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  )
                })}
              </div>
            )
          })}
        </nav>
      </div>

      <div style={styles.bottom}>
        {/* DevTools panel — local dev only. Stripped from production builds. */}
        {import.meta.env.DEV && <DevTools />}
        {/* Legal links (Privacy / Terms) live inside the BrandMenu popover —
            they're account chrome, not nav. Putting them at the bottom of the
            sidebar (the previous location) made them visually misaligned with
            the left-aligned nav items above and competed for attention with
            the BMC Max upsell. Notion / Linear / Vercel pattern. */}
        <BrandMenu signOut={signOut} />
      </div>
    </aside>
    </>
  )
}

// =============================================================================
// BrandMenu — bottom-of-sidebar brand identity + account menu
// =============================================================================
// Replaces the previous top-of-sidebar brand switcher AND the bare Sign Out
// button. Anchors the brand identity to the bottom (matches old BMC
// dashboard pattern + Notion / Linear / Vercel sidebar conventions).
// Clicking the brand row toggles a popover that floats above with: Plan
// badge, Settings, Billing, Sign Out. Click outside or the brand row again
// to dismiss.
function BrandMenu({ signOut }: { signOut: () => void }) {
  const { brand, isAdmin } = useBrand()
  const { account, isAgency, isAccountOwner, canManageAccount, brands, activeBrandId, inBrand, enterBrand, exitToAgency } = useAccount()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [switchingAgency, setSwitchingAgency] = useState(false)
  const [checkout, setCheckout] = useState<WhopCheckoutParams | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  // With the list capped, the active brand (and the actions nested under it) can
  // start out scrolled past — which is the same "where did it go" problem in a
  // smaller form. `activeBrandRef` pulls it into view when the menu opens (see
  // the effect below).
  //
  // Declared HERE, with the other hooks, and not down beside the JSX that uses
  // it: everything below `if (!brand && !account) return null` runs only on the
  // renders that get past it. This component is mounted while a brand is still
  // loading (and it owns the agency checkout modal, so it is mounted across a
  // subscribe), so it renders null first and then re-renders with data — and a
  // hook after that return would make the second render call MORE hooks than
  // the first. That is the "Rendered more hooks than during the previous
  // render" crash that took the whole app down right after checkout.
  const activeBrandRef = useRef<HTMLDivElement | null>(null)

  // Click outside to close. Listening on document because the popover renders
  // inside the sidebar but might overflow.
  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Scroll the active brand into view when the menu opens. `block: 'nearest'`
  // so it only scrolls when the item genuinely isn't visible.
  useEffect(() => {
    if (!open) return
    const el = activeBrandRef.current
    if (!el) return
    // After paint, so the scroller has its final height.
    const id = requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest' }))
    return () => cancelAnimationFrame(id)
  }, [open])

  // Render for a brandless agency too (account but no brand) so the owner still
  // has sign-out + add-brand + activate. Only bail when there's neither.
  if (!brand && !account) return null

  // "In a brand context" = a solo brand (always), or an agency that has ENTERED
  // a brand (inBrand). At the agency lobby (agency && !inBrand) there is NO active
  // brand, so the brand-scoped menu items (Billing/Team/Activity/Settings) are
  // hidden and the bottom row shows the AGENCY identity, not a brand.
  const inBrandContext = !isAgency || inBrand
  // For agency accounts the bottom row reflects the ACTIVE brand only when inside
  // one; at the lobby it shows the agency. Solo accounts show their one brand.
  const activeBrand = isAgency ? brands.find(b => b.id === activeBrandId) : null
  const displayName = inBrandContext
    ? (activeBrand?.brand_name ?? brand?.brand_name ?? account?.name ?? 'Account')
    : (account?.name ?? 'Your agency')
  const displayInitial = displayName.charAt(0).toUpperCase()
  // Avatar reflects the ACTIVE brand when inside a brand; at the agency lobby it
  // shows the AGENCY's own logo (account.logo_url), falling back to the agency
  // initial circle. (Never fall back to the logged-in brand's logo in agency
  // mode — a wrong-brand-hiring risk.)
  const displayLogo = !inBrandContext
    ? (account?.logo_url ?? null)
    : (isAgency ? (activeBrand?.logo_url ?? null) : (brand?.logo_url ?? null))

  const go = (path: string) => {
    setOpen(false)
    navigate(path)
  }
  // Self-serve agency upgrade: start the $997 Whop checkout for this brand's
  // account, then redirect. On payment the webhook flips the account to
  // type='agency' (and cancels the old $497). Used by both "Switch to Agency"
  // (solo brand) and "Activate Agency hiring" (a free agency with no sub).
  const handleAgencyCheckout = async () => {
    if (switchingAgency || (!brand && !account)) return
    setSwitchingAgency(true)
    try {
      // Brand→agency switch resolves the account from the brand; a brandless
      // free agency activates its $997 against the account directly.
      const res = await createAgencyCheckout(brand ? { brandId: brand.id } : { accountId: account!.id })
      // Embedded checkout in-app (hosted pages retired). Staging-bypass returns
      // only { url } → redirect.
      if (res.session_id && res.plan_id) {
        setCheckout({
          planId: res.plan_id,
          sessionId: res.session_id,
          email: res.email ?? null,
          environment: res.environment,
          returnUrl: AGENCY_CHECKOUT_RETURN_URL,
        })
        setSwitchingAgency(false)
        setOpen(false)
      } else if (res.url) {
        window.location.href = res.url
      } else {
        throw new Error('No checkout returned')
      }
    } catch {
      setSwitchingAgency(false)
      window.alert('Could not start the agency upgrade. Please try again in a moment.')
    }
  }
  const switchBrand = async (id: string) => {
    // Only block after the paid period has ENDED (cancelled AND sub cleared).
    // During cancel→period-end the sub is still set, so brand switching still
    // works. AgencyAccessGuard is the backstop once it truly ends.
    if (isAgency && account?.plan_status === 'canceled' && !account?.whop_subscription_id) { setOpen(false); return }
    // enterBrand re-checks the access; a stale menu entry refuses rather than
    // opening a brand where every request 403s. Refused → the lobby, which is
    // where the explanation is (and which is the honest destination anyway).
    const ok = await enterBrand(id)
    setOpen(false)
    navigate(ok ? '/' : '/agency')
  }

  // Brand-scoped actions (act on the ACTIVE brand). Rendered nested under the
  // active brand for an agency (see below), or flat for a solo brand.
  const brandScopedMenu = (
    <>
      {isAdmin && (
        <button className="brand-menu-item" style={styles.brandMenuItem} onClick={() => go('/billing')}>
          <CreditCard size={14} strokeWidth={1.5} />
          <span>Billing</span>
        </button>
      )}
      {isAdmin && (
        <button className="brand-menu-item" style={styles.brandMenuItem} onClick={() => go('/team')}>
          <Users size={14} strokeWidth={1.5} />
          <span style={{ flex: 1 }}>Team</span>
          <span style={styles.newBadge}>New</span>
        </button>
      )}
      {isAdmin && (
        <button className="brand-menu-item" style={styles.brandMenuItem} onClick={() => go('/activity')}>
          <ScrollText size={14} strokeWidth={1.5} />
          <span style={{ flex: 1 }}>Activity log</span>
          <span style={styles.newBadge}>New</span>
        </button>
      )}
      <button className="brand-menu-item" style={styles.brandMenuItem} onClick={() => go('/settings')}>
        <SettingsIcon size={14} strokeWidth={1.5} />
        <span>Settings</span>
      </button>
    </>
  )

  return (
    <>
    {checkout && (
      <WhopCheckoutModal
        params={checkout}
        onClose={() => setCheckout(null)}
        onComplete={() => { window.location.href = checkout.returnUrl }}
      />
    )}
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Popover — floats above the brand row when open. Plan info lives
          on the Billing page now (was here as <PlanBadge>, but that
          component flashed a "Start free trial" button for paid users
          while it loaded the real tier — see the screenshot bug from
          2026-05-02 QA). The right place for plan info is Billing
          alongside the upgrade flow anyway. */}
      {open && (
        <div className="brand-menu-popover-scroll" style={styles.brandMenuPopover}>
          {/* Brand-scoped items — only when inside a brand. At the agency lobby
              (agency && !inBrand) there's no active brand, so these are hidden;
              they reappear once the user enters a brand. */}
          {isAgency && (
            <>
              <button className="brand-menu-item" style={styles.brandMenuItem} onClick={() => { exitToAgency(); go('/agency') }}>
                <LayoutGrid size={14} strokeWidth={1.5} />
                <span style={{ flex: 1 }}>Agency home</span>
                {!inBrand && <Check size={14} strokeWidth={2} color="#FB3AAE" />}
              </button>
              <div style={styles.brandMenuDivider} />
              <div className="brand-menu-scroll" style={styles.brandMenuList}>
              {brands.map(b => {
                const isActive = inBrand && b.id === activeBrandId
                return (
                  <div key={b.id} ref={isActive ? activeBrandRef : undefined}>
                    <button
                      className="brand-menu-item"
                      style={{ ...styles.brandMenuItem, ...(isActive ? styles.brandMenuItemActive : {}) }}
                      onClick={() => switchBrand(b.id)}
                    >
                      {b.logo_url
                        ? <img src={b.logo_url} alt="" style={styles.menuBrandAvatarImg} />
                        : <span style={styles.menuBrandAvatar}>{b.brand_name.charAt(0).toUpperCase()}</span>}
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.brand_name}</span>
                      {isActive && <Check size={14} strokeWidth={2} color="#FB3AAE" />}
                    </button>
                    {/* Brand-scoped actions nest UNDER the active brand so it's
                        unmistakable they act on THIS brand — a gradient bar + tinted
                        card visually bind them to the selected brand above. */}
                    {isActive && (
                      <div style={styles.brandScopeWrap}>
                        <div style={styles.brandScopeBar} />
                        <div style={styles.brandScopeCard}>
                          {brandScopedMenu}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
              </div>
            </>
          )}

          {/* Solo brand → its brand-scoped actions render flat (no switcher). */}
          {!isAgency && brandScopedMenu}

          {/* Solo brand → self-serve upgrade to Agency ($997). OWNER-only: it's a
              billing action, and gating on isOwner (not isAdmin) hides it from a
              brand-scoped team member whose brand is already under an agency (they
              fall back to a solo view but are NOT the owner). Agencies add brands
              from the Agency Home lobby. */}
          {!isAgency && isAccountOwner && (
            <button className="brand-menu-item" style={styles.brandMenuItem} onClick={() => go('/agency/add-brand')}>
              <Plus size={14} strokeWidth={1.5} />
              {/* nowrap keeps the label on one line — without it the flexed label
                  wrapped to a second line (see the two-line QA screenshot). The
                  label + badge fit the 260px sidebar popover; flexShrink:0 on the
                  badge means the label absorbs any shortfall instead of the badge. */}
              <span style={{ flex: 1, whiteSpace: 'nowrap' }}>Upgrade to Agency</span>
              <span style={{ ...styles.newBadge, flexShrink: 0 }}>New</span>
            </button>
          )}
          {/* Free agency (no subscription yet) → the owner or a co-owner activates
              $997 to unlock hiring (the worker's checkout gate is the same pair).
              Own leading divider so it separates from the brand list. */}
          {isAgency && canManageAccount && !account?.whop_subscription_id && (
            <>
              <div style={styles.brandMenuDivider} />
              <button className="brand-menu-item" style={styles.brandMenuItem} onClick={handleAgencyCheckout} disabled={switchingAgency}>
                <CreditCard size={14} strokeWidth={1.5} />
                {/* nowrap keeps this on one line. "Activate Agency hiring" + the
                    $997 badge overflows the 260px sidebar popover, so the label is
                    trimmed to "Activate hiring" ("Agency" is redundant — this only
                    renders in the agency menu). flexShrink:0 protects the badge. */}
                <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{switchingAgency ? 'Starting…' : 'Activate hiring'}</span>
                {!switchingAgency && <span style={{ ...styles.newBadge, flexShrink: 0 }}>$997</span>}
              </button>
            </>
          )}
          <div style={styles.brandMenuDivider} />
          {/* Legal — /privacy and /terms are matched by the same top-level
              <Routes> in App.tsx (ahead of the authed catch-all), so a plain
              client-side Link swaps straight to them without a full reload. */}
          <Link
            className="brand-menu-item"
            style={{ ...styles.brandMenuItem, textDecoration: 'none' }}
            to="/privacy"
            onClick={() => setOpen(false)}
          >
            <FileText size={14} strokeWidth={1.5} />
            <span>Privacy Policy</span>
          </Link>
          <Link
            className="brand-menu-item"
            style={{ ...styles.brandMenuItem, textDecoration: 'none' }}
            to="/terms"
            onClick={() => setOpen(false)}
          >
            <FileText size={14} strokeWidth={1.5} />
            <span>Terms & Conditions</span>
          </Link>
          <div style={styles.brandMenuDivider} />
          <button
            className="brand-menu-item brand-menu-item--danger"
            style={{ ...styles.brandMenuItem, color: '#ef4444' }}
            onClick={() => { setOpen(false); signOut() }}
          >
            <LogOut size={14} strokeWidth={1.5} />
            <span>Sign out</span>
          </button>
        </div>
      )}

      {/* Brand row — the always-visible bottom anchor. Refined 2026-05-02
          per design feedback: true-circle avatar, BMC brand-gradient fill,
          tighter type ladder, more breathing room, soft hover instead of
          a bright outline ring when expanded. */}
      <button
        onClick={() => setOpen(o => !o)}
        className="brand-row-button"
        style={{ ...styles.brandSwitcher, width: '100%', background: open ? 'rgba(255, 255, 255, 0.06)' : 'transparent' }}
      >
        {displayLogo ? (
          <img
            src={displayLogo}
            alt={displayName}
            style={styles.brandAvatarImg}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none'
              const sibling = (e.currentTarget as HTMLImageElement).nextElementSibling as HTMLElement | null
              if (sibling) sibling.style.display = 'flex'
            }}
          />
        ) : null}
        <div style={{
          ...styles.brandAvatar,
          display: displayLogo ? 'none' : 'flex',
        }}>
          {displayInitial}
        </div>
        <div style={styles.brandInfo}>
          <div style={styles.brandName}>{displayName}</div>
          {isAgency && account ? (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>{account.name}</div>
          ) : (
            <div style={styles.brandCaption}>{brand?.category || 'View account'}</div>
          )}
        </div>
        <ChevronUp size={14} style={{
          color: 'var(--text-tertiary)',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s ease',
          flexShrink: 0,
        }} />
      </button>
    </div>
    </>
  )
}
